import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getSessionFromCookie } from "../../utils/cognito";
import { BillingInfo } from "../../../types/addServiceTypes";
import {
  EanOwnershipRow,
  INJECTION_PARAM_FIELDS,
  OFFTAKE_PARAM_FIELDS,
  SourceSelection,
  SwitchInjectionParams,
  SwitchOfftakeParams,
  SwitchOverviewResponse,
  SwitchParamCandidate,
  SwitchParamProvenance,
  CUSTOMER_SWITCH_ERROR_KEYS,
  buildSecondLegContractPayload,
  buildSecondLegOverviewPayload,
  buildSwitchLegContractPayload,
  buildSwitchLegOverviewPayload,
  findDirectionServiceConfig,
  isCandidateSigned,
  isSecondLegPayloadSafe,
  missingWrittenDirections,
  needsSourcePicker,
  pickDefaultCandidateId,
  readPostSwitchEanRows,
  resolveAskPhaseDirection,
  shouldShowServerDetail,
  isAskPhaseUndetermined,
  selectsInsertBranch,
  readGroupMembers,
  groupMemberSetMatches,
  membersMissingContracts,
} from "./customerSwitchPayloads";
import { usePartnerData } from "../../../types/usePartnerData";
import { getMinEndDate, isValidBasicStartDate } from "../../../utils/dateUtils";
import {
  ContractIdMap,
  buildContractIdFields,
  mergeContractIds,
  readAddContractIds,
} from "../../../utils/contractIdEcho";
import { InjectionContractParameters } from "../../addServices/InjectionContractParameters";
import { OfftakeContractParameters } from "../../addServices/OfftakeContractParameters";
import type { InjectionFormData, OfftakeFormData } from "../../../pages/AddService";

// FT — admin-only "Customer Switch". An admin is onboarding an EAN that is
// active/future under ANOTHER company. The modal opens STRAIGHT into the parameter
// preview (there is no option-select screen: the admin's intent is already captured by
// the "Customer Switch" CTA that opens it, and the only ever-enabled option was "keep
// current parameters"). "Confirm & create" on the preview then runs the REAL onboarding
// sequence programmatically and GENERATES the proposal PDFs. It never signs and never
// renders DigitalSignatureModal — see runCustomerSwitch stage 3.
// The parameters are BE-authoritative: contractOverview (in customerSwitch mode)
// resolves them + billingInfo + effective date; addContract stamps boundary B and
// returns it as `switchDate`.

// One onboarding leg. contract_type === direction in every case.
//
// S6: the caller (resolveCustomerSwitchLegs) emits EXACTLY ONE leg in every reachable
// case — `combined` when Company A holds both directions (regardless of SC_OFFINJE),
// otherwise the single direction A holds, and none at all when the EAN has two
// different foreign owners. The array shape is retained because the runner is
// leg-agnostic, but a two-leg split is NOT produced: it would be the FT-3581-class
// trap, since leg 1 ends BOTH of A's directions and leg 2 would then find zero foreign
// rows and silently degrade to a normal onboarding.
export interface CustomerSwitchLeg {
  direction: "injection" | "offtake" | "combined";
  injectionServiceConfig?: string;
  offtakeServiceConfig?: string;
}

interface CustomerSwitchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ean: string;
  /**
   * 🔴 Company B — the RECEIVING company. Every call below uses it EXCEPT the
   * preswitch, which the caller performs against company A. In the onboarding wizard
   * these were one value (the admin stood in B); on the Manage tab the user stands in
   * A, so a bare `companyId` here is forbidden.
   */
  destinationCompanyId: number;
  // The whole preswitch response — posted verbatim to addSdps, exactly as the
  // normal handleContinue path does.
  pslFullData: unknown;
  /**
   * Additional connection points travelling in the SAME transaction, each with its
   * OWN preswitch response for its own addSdps call. Empty/absent = a single-point
   * switch, byte-identical to what the wizard has always sent.
   */
  groupMembers?: Array<{
    ean: string;
    pslFullData: unknown;
    injectionServiceConfig: string | null;
    offtakeServiceConfig: string | null;
    directions: Array<"injection" | "offtake">;
  }>;
  legs: CustomerSwitchLeg[];
  signerName: string;
  companyEmail: string;
  // Ask-phase (§5a) inputs, read-only. The popup owns the second leg's FORM state
  // itself — these only supply what the preswitch already told us about the point.
  sdpAtriasConfig?: string[];
  technologies?: string;
  companyName?: string;
  addressLine?: {
    apStreetName: string;
    apApartmentNumber: string;
    apZip: string;
    apCity: string;
    country: string;
    runningProcessLabel: string;
    gsrn: string;
  } | null;
}

// S5d: "preview" is the modal's first interactive screen and sits in front of the write,
// and it is the ONLY confirmation gate for it. contractOverview in
// customer-switch mode is a PURE READ on the backend (findForeignActiveSdps,
// isAdminContact, getCustomerSwitchSourceParams, BillingInformationQuery are all
// SELECT-only), so the admin can see exactly which parameters will be written — and
// change which contract they are inherited from — before anything is persisted.
// "ask"/"secondLegParams"/"secondLegRunning" are the §5a ask-phase: after the switch leg
// is signed, offer the direction Company A did NOT hold. That direction is a PLAIN
// ONBOARDING, not a switch — see buildSecondLegContractPayload.
type Phase =
  | "loadingPreview"
  | "preview"
  | "running"
  | "ask"
  | "secondLegParams"
  | "secondLegRunning"
  | "done"
  | "error";
type StageKey = "addEan" | "addContract" | "proposal";
type StageState = "pending" | "active" | "complete" | "failed";

const STAGE_ORDER: StageKey[] = ["addEan", "addContract", "proposal"];

/**
 * Every `CustomerSwitchError` code the backend can throw, mapped to a localised message.
 * Verified against the complete set of `new CustomerSwitchError(...)` constructions in
 * yuso-portals-backend (`functions/contracts.ts` + `functions/customerSwitch.ts`) —
 * 7 codes. Without a branch here the backend's raw ENGLISH sentence is shown verbatim to
 * FR/NL admins, and three of these are the stop guards this task added, so they are the
 * codes most likely to fire.
 */

interface DescribedError {
  message: string;
  /** The backend's own sentence, shown BENEATH the localised copy — never instead of it. */
  detail?: string;
}

/**
 * addContract answered 200 but reported no installation_id for a direction we asked for,
 * so no contract row exists. Distinct type so it cannot be swallowed into the generic
 * error message — and so the stage fails loudly instead of generating a proposal PDF for
 * a contract that was never created.
 */
class ContractNotCreatedError extends Error {
  readonly directions: string[];
  constructor(directions: string[]) {
    super(`addContract returned no installation_id for: ${directions.join(", ")}`);
    this.name = "ContractNotCreatedError";
    this.directions = directions;
  }
}

/**
 * 🔴 A POST-COMMIT PROPOSAL FAILURE IS NOT A SWITCH FAILURE.
 *
 * The proposal documents are generated AFTER the backend transaction has committed —
 * N+1 independent POSTs (the primary point, plus one per group member). A raw
 * network/5xx on any of them used to land in the same catch as the switch itself, with
 * `failedPhase = "running"` and `switchCommitted = true`, so `canRetry` refused: no
 * Retry button, no indication of WHICH point is missing its document. The admin had to
 * notice on their own and regenerate by hand from PortfolioServices. Grouping turned
 * one failure opportunity into N+1.
 *
 * This type is what lets the two be told apart. It carries the ORIGINAL error (so the
 * backend's code/message still drives `describeError`) and the EANs that still have no
 * proposal, and it is raised ONLY from `generateProposals` — never from the switch
 * write. The switch-replay block in `canRetry` is untouched.
 */
class ProposalGenerationError extends Error {
  /** The underlying axios/network error — `describeError` must still see it. */
  readonly original: unknown;
  /** Points whose proposal has NOT been generated, in send order, deduped. */
  readonly missingEans: string[];
  constructor(original: unknown, missingEans: string[]) {
    super(`proposal generation failed for: ${missingEans.join(", ")}`);
    this.name = "ProposalGenerationError";
    this.original = original;
    this.missingEans = missingEans;
  }
}

/** One `generateContract/proposal` POST, retryable on its own. */
interface PendingProposal {
  ean: string;
  /** "injection" | "offtake" | "combined" — the endpoint is keyed on (ean, direction). */
  direction: string;
  billingInfo: BillingInfo | null;
}

/** The endpoint's own identity, so a two-leg point cannot collide on the EAN alone. */
const proposalKey = (p: PendingProposal): string => `${p.ean}::${p.direction}`;

/**
 * `failedPhase` is a FAILURE MARKER, not a render phase — "generatingProposals" is a
 * post-commit step INSIDE `running` that is independently resumable, and it must never
 * become a value of `Phase` or it would start driving the render (and `dismissable`).
 */
type FailedPhase = Phase | "generatingProposals";

// Blank second-leg forms. Deliberately NOT seeded from the switch leg: the second
// direction is a plain onboarding whose parameters the admin enters, and pre-filling it
// from A's cloned values would be exactly the cross-direction copy §4a forbids
// (offtake fees must never seed an injection contract).
const EMPTY_INJECTION_FORM: InjectionFormData = {
  startDate: "",
  endDate: "",
  isFlexible: true,
  annualVolume: null,
  annualVolumeUnknown: false,
  product: "1",
  priceGSC: null,
  priceWKC: null,
  platformFee: null,
  partnerId: 1,
  partnerContactId: null,
  partnerFee: null,
  valuesConfirmed: false,
};

const EMPTY_OFFTAKE_FORM: OfftakeFormData = {
  startDate: "",
  endDate: "",
  isFlexible: true,
  annualVolume: null,
  annualVolumeUnknown: false,
  product: "1",
  priceGSC: null,
  priceWKC: null,
  balancingFee: null,
  partnerId: 1,
  partnerContactId: null,
  partnerFee: null,
  priceIndex: null,
  valuesConfirmed: false,
};

const StageRow: React.FC<{ label: string; state: StageState; suffix?: string }> = ({ label, state, suffix }) => {
  let indicator: React.ReactNode;
  if (state === "complete") {
    indicator = (
      <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M5 13l4 4L19 7" />
      </svg>
    );
  } else if (state === "active") {
    indicator = (
      <svg className="w-5 h-5 text-primary-600 animate-spin" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    );
  } else if (state === "failed") {
    indicator = (
      <svg className="w-5 h-5 text-[#D04D4D]" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    );
  } else {
    indicator = <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-300" />;
  }

  return (
    <div className="flex items-center gap-3 py-2">
      <span className="flex items-center justify-center w-6">{indicator}</span>
      <span
        className={`text-sm font-medium ${
          state === "complete"
            ? "text-primary-600"
            : state === "active"
              ? "text-primary-700"
              : state === "failed"
                ? "text-[#D04D4D]"
                : "text-gray-400"
        }`}
      >
        {label}
        {suffix ? ` ${suffix}` : ""}
      </span>
    </div>
  );
};

// S5d / §4c — shows the admin exactly which values will be written and WHERE EACH CAME
// FROM (the landing company's own contract, the outgoing company's, or standard
// defaults) before the money is committed.
const ProvenanceBadge: React.FC<{ provenance: SwitchParamProvenance }> = ({ provenance }) => {
  const { t } = useTranslation(["management", "addservice"]);
  const tone =
    provenance.source === "B"
      ? "bg-green-100 text-green-800"
      : provenance.source === "A"
        ? "bg-yellow-100 text-yellow-800"
        : "bg-gray-100 text-gray-700";
  const label =
    provenance.source === "B"
      ? t("management:customerSwitch.sourceB")
      : provenance.source === "A"
        ? t("management:customerSwitch.sourceA")
        : t("management:customerSwitch.sourceDefaults");
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${tone}`}>{label}</span>;
};

const ParamRow: React.FC<{ label: string; value: number | null }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-1 border-b border-gray-100 last:border-0">
    <span className="text-xs text-primary-500">{label}</span>
    <span className="text-sm font-medium text-primary-700 tabular-nums">{value === null ? "—" : value}</span>
  </div>
);

const DirectionParams: React.FC<{
  direction: "injection" | "offtake";
  params: SwitchInjectionParams | SwitchOfftakeParams;
  provenance: SwitchParamProvenance | null;
  candidates?: SwitchParamCandidate<SwitchInjectionParams | SwitchOfftakeParams>[];
  selectedContractId?: number;
  onSelectSource: (contractId: number) => void;
}> = ({ direction, params, provenance, candidates, selectedContractId, onSelectSource }) => {
  const { t } = useTranslation(["management", "addservice"]);
  const fields: string[] =
    direction === "injection" ? [...INJECTION_PARAM_FIELDS] : [...OFFTAKE_PARAM_FIELDS];
  const record = params as unknown as Record<string, number | null>;

  // §4c provenance, completed: the candidate the SERVER actually resolved from. Keyed on
  // the provenance contract id first so the warning is about what will really be written,
  // and it fires even when there is only one candidate (no picker rendered).
  const activeCandidate = candidates?.find(
    (c) => c.contractId === (provenance?.contractId ?? selectedContractId)
  );
  const unsignedSource = !!activeCandidate && !isCandidateSigned(activeCandidate);

  const optionLabel = (c: SwitchParamCandidate<SwitchInjectionParams | SwitchOfftakeParams>) =>
    t(isCandidateSigned(c) ? "management:customerSwitch.pickSourceOption" : "management:customerSwitch.pickSourceOptionUnsigned", {
      contractId: c.contractId,
      date: c.startDate || "—",
    });

  return (
    <div className="flex-1 rounded-lg border border-gray-200 dark:border-primary-700 p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-primary-600 font-semibold text-sm">
          {t(direction === "injection" ? "addservice:services.injection" : "addservice:services.offtake")}
        </h3>
        {provenance && <ProvenanceBadge provenance={provenance} />}
      </div>

      {provenance && provenance.source !== "defaults" && (
        <p className="text-xs text-primary-400 mb-3">
          {provenance.companyName || "—"}
          {provenance.ean ? ` · EAN ${provenance.ean}` : ""}
          {provenance.contractId !== null ? ` · #${provenance.contractId}` : ""}
          {provenance.startDate ? ` · ${t("management:customerSwitch.provenanceSince", { date: provenance.startDate })}` : ""}
        </p>
      )}

      {/* §4c: when the landing company holds more than one active contract for this
          direction, the admin picks which one's parameters to inherit. Changing the pick
          re-resolves server-side, so the values shown always match the selection. */}
      {needsSourcePicker(candidates) && candidates && (
        <div className="mb-3">
          <label
            htmlFor={`source-${direction}`}
            className="block text-primary-600 mb-1 font-medium text-xs uppercase tracking-wide"
          >
            {t("management:customerSwitch.pickSourceLabel")}
          </label>
          <select
            id={`source-${direction}`}
            value={selectedContractId ?? ""}
            onChange={(e) => onSelectSource(Number(e.target.value))}
            className="w-full border rounded-md px-3 py-2 border-gray-200 text-primary-600 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
          >
            {candidates.map((c) => (
              <option key={c.contractId} value={c.contractId}>
                {optionLabel(c)}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">{t("management:customerSwitch.pickSourceHelp")}</p>
        </div>
      )}

      {/* Never blocks the pick — an admin may have a legitimate reason — but an unsigned
          source means these fees were never agreed, so it must be an informed choice. */}
      {unsignedSource && (
        <div className="mb-3 rounded-md bg-yellow-50 border border-yellow-200 p-2">
          <p className="text-xs text-yellow-800">{t("management:customerSwitch.unsignedSourceWarning")}</p>
        </div>
      )}

      <div>
        {fields.map((f) => (
          <ParamRow key={f} label={t(`management:customerSwitch.fields.${f}`)} value={record[f] ?? null} />
        ))}
      </div>
    </div>
  );
};

export const CustomerSwitchModal: React.FC<CustomerSwitchModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  ean,
  destinationCompanyId,
  pslFullData,
  groupMembers = [],
  legs,
  signerName,
  companyEmail,
  sdpAtriasConfig,
  technologies,
  companyName,
  addressLine,
}) => {
  const { t } = useTranslation(["management", "addservice"]);

  // §5g: the popup owns the second leg's data end-to-end. The partner cluster comes from
  // the same hook the wizard uses, so partner lookup/contacts behave identically without
  // sharing any state with the wizard's own step 3.
  const {
    selectedPartnerLocal,
    partnerContacts,
    isAdminOrPartner,
    handlePartnerChange,
    handlePartnerContactChange,
    partners,
    user,
  } = usePartnerData();

  const [secondLegInjectionForm, setSecondLegInjectionForm] = useState<InjectionFormData>(
    () => ({ ...EMPTY_INJECTION_FORM })
  );
  const [secondLegOfftakeForm, setSecondLegOfftakeForm] = useState<OfftakeFormData>(() => ({
    ...EMPTY_OFFTAKE_FORM,
  }));
  const [partnerSearchTerm, setPartnerSearchTerm] = useState("");
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [lastSelectedInjectionPartnerId, setLastSelectedInjectionPartnerId] = useState<number | null>(null);
  const [lastSelectedOfftakePartnerId, setLastSelectedOfftakePartnerId] = useState<number | null>(null);
  const [showVolumeConfirmation, setShowVolumeConfirmation] = useState(false);
  const [formValidationError, setFormValidationError] = useState<string | null>(null);

  const [phase, setPhase] = useState<Phase>("loadingPreview");
  const [stages, setStages] = useState<Record<StageKey, StageState>>({
    addEan: "pending",
    addContract: "pending",
    proposal: "pending",
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [switchDate, setSwitchDate] = useState<string | null>(null);
  const [legProgress, setLegProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  // The resolved parameters / provenance / candidate sources returned by
  // contractOverview in customer-switch mode (§4c). Null until leg 1's overview lands.
  const [resolution, setResolution] = useState<SwitchOverviewResponse | null>(null);
  // The admin's per-direction pick of which of B's active contracts to inherit from.
  const [sourceSelection, setSourceSelection] = useState<SourceSelection>({});
  // True once the optional second direction has been created + signed, so the success
  // screen can say both contracts are in place.
  const [secondLegDone, setSecondLegDone] = useState(false);
  /**
   * True when a SINGLE-direction switch committed but the ask-phase was skipped WITHOUT
   * knowing whether the other direction could be offered — either the EAN's post-switch
   * ownership rows could not be read, or the point's service components are unknown so we
   * cannot tell whether that direction exists at all. Without this the skip is
   * indistinguishable from a legitimate "nothing to offer", and an admin testing a partial
   * case cannot tell correct behaviour from a silent failure.
   */
  const [askUndetermined, setAskUndetermined] = useState(false);
  /**
   * Which phase failed, so Retry RESUMES it. Retry used to be hard-wired to
   * runCustomerSwitch, so a failure during the PURE-READ preview was "retried" by
   * performing the un-previewed WRITE — skipping the parameter review the preview exists
   * for. null means the failure is not retryable (bad inputs).
   */
  const [failedPhase, setFailedPhase] = useState<FailedPhase | null>(null);
  /**
   * The EANs whose proposal document is still missing after a post-commit proposal
   * failure. Rendered on the error screen: telling the admin WHICH point to look at is
   * the part that costs them the most today.
   */
  const [missingProposalEans, setMissingProposalEans] = useState<string[]>([]);
  /** The backend transaction has committed. The switch must never be replayed. */
  const [switchCommitted, setSwitchCommitted] = useState(false);
  /**
   * EANs whose SDP has already been created under company B in this attempt. Read
   * by the addSdps loop so a Retry after a PARTIAL group failure never re-posts a
   * preswitch response over a row that already exists. A ref, not state: it must
   * survive the re-render that Retry triggers, and nothing renders from it.
   */
  const sdpsWritten = useRef<Set<string>>(new Set());
  /**
   * Proposals already generated in this attempt, keyed `ean::direction`. SAME PATTERN as
   * `sdpsWritten` — a ref, because it must survive the re-render Retry triggers and
   * nothing renders from it — so a retry regenerates ONLY what is still missing and can
   * never produce a duplicate document for a point that already succeeded.
   */
  const proposalsGenerated = useRef<Set<string>>(new Set());
  /**
   * The proposal POSTs of the FINAL leg, kept so Retry can resume them WITHOUT touching
   * the switch write. Empty ⇒ no proposal step is resumable and a failure falls back to
   * the ordinary (switch-committed, non-retryable) path.
   */
  const pendingProposals = useRef<PendingProposal[]>([]);
  /**
   * What the success tail needs once the proposals finally land: the switch date the
   * backend stamped, and the post-commit ownership rows the ask-phase is decided from.
   * Captured before the proposal step, because a failure there means the tail never ran.
   */
  const pendingCompletion = useRef<{ switchDate: string | null; eanRows: EanOwnershipRow[] | null } | null>(null);
  // The second-leg addContract has committed a contract. A retry after a post-commit
  // failure (e.g. the proposal call) must resume from the failed step and NEVER re-POST
  // addContract — a second POST would create a DUPLICATE contract. Mirrors switchCommitted.
  const [secondLegCommitted, setSecondLegCommitted] = useState(false);
  /**
   * The contract ROW ids `/addContract` reported back in this attempt, per direction.
   *
   * The backend no longer infers which row to update from the `latest` flag — it
   * updates the row the client names. A ref for the same reason as `sdpsWritten`: it
   * must survive the re-render Retry triggers and nothing renders from it.
   *
   * Only the SECOND leg ever sends one back. The switch leg's id is recorded but never
   * re-sent: its write goes through `performCustomerSwitch`, and the ask-phase only
   * offers the OTHER direction — an id sent for a direction a request is not acting on
   * is a 400 (`CONTRACT_ID_DIRECTION_MISMATCH`) by design.
   */
  const contractIdsWritten = useRef<ContractIdMap>({});
  // The EAN's ownership rows re-read by the backend AFTER the handover committed. This is
  // the ONLY trustworthy view of who holds what once the switch has run — the preswitch
  // snapshot in pslFullData still describes pre-switch ownership. Drives both the
  // ask-phase eligibility check (F2) and the second leg's installation_id (F1).
  const [postSwitchEanRows, setPostSwitchEanRows] = useState<EanOwnershipRow[] | null>(null);

  // Reset and load the parameter preview whenever the modal is (re)opened. There is no
  // longer a choice screen in front of it: the admin's intent is already captured by the
  // "Customer Switch" CTA that opens this modal (AddService.tsx — the ONLY thing that
  // opens it), and the old screen's only enabled option was "keep parameters". What
  // auto-runs here is contractOverview in switch mode, which is a PURE READ — the write
  // still sits behind "Confirm & create" on the preview, which is now the single
  // confirmation gate.
  useEffect(() => {
    if (isOpen) {
      setPhase("loadingPreview");
      setStages({ addEan: "pending", addContract: "pending", proposal: "pending" });
      setErrorMessage(null);
      setErrorDetail(null);
      setSwitchDate(null);
      setLegProgress({ current: 0, total: 0 });
      setResolution(null);
      setSourceSelection({});
      setSecondLegDone(false);
      setAskUndetermined(false);
      setFailedPhase(null);
      setSwitchCommitted(false);
      // A fresh open is a fresh attempt; only a RETRY within one open reuses it.
      sdpsWritten.current = new Set();
      proposalsGenerated.current = new Set();
      pendingProposals.current = [];
      pendingCompletion.current = null;
      setMissingProposalEans([]);
      setSecondLegCommitted(false);
      setPostSwitchEanRows(null);
      setSecondLegInjectionForm({ ...EMPTY_INJECTION_FORM });
      setSecondLegOfftakeForm({ ...EMPTY_OFFTAKE_FORM });
      setPartnerSearchTerm("");
      setShowPartnerDropdown(false);
      setShowVolumeConfirmation(false);
      setFormValidationError(null);
      // Explicitly {} and not `sourceSelection`: the reset above clears it in this same
      // pass, so reading the state variable here would pass the PREVIOUS open's picks.
      void loadPreview({});
    }
    // Deps stay [isOpen] deliberately. loadPreview is re-created on every render, so
    // adding it (as exhaustive-deps suggests) would re-fire this effect every render and
    // put contractOverview into an infinite request loop. This must run ONCE per open.
  }, [isOpen]);

  const setStage = (key: StageKey, state: StageState) => setStages((prev) => ({ ...prev, [key]: state }));

  const apiBase = import.meta.env.VITE_API_SERVICES;

  const authHeaders = () => {
    const session = getSessionFromCookie();
    const token = session?.getAccessToken().getJwtToken();
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  };

  const describeError = (err: unknown): DescribedError => {
    // A contract that was never written must never read as a generic failure.
    if (err instanceof ContractNotCreatedError) {
      return { message: t("management:customerSwitch.errorNoContractCreated", { directions: err.directions.join(", ") }) };
    }
    let message = t("management:customerSwitch.errorGeneric");
    let detail: string | undefined;
    if (axios.isAxiosError(err)) {
      const data = err.response?.data as { error?: string; code?: string; message?: string } | undefined;
      const key = data?.code ? CUSTOMER_SWITCH_ERROR_KEYS[data.code] : undefined;
      if (key) {
        message = t(key);
        // Keep the localised headline AND the server's specifics.
        if (shouldShowServerDetail(data?.code)) {
          detail = data?.error || data?.message || undefined;
        }
      } else if (data?.error || data?.message) {
        // Last resort only: the backend's raw English sentence — UNLOCALISED, and it may
        // contain internal ids. Reaching this means the backend raised a code that
        // CUSTOMER_SWITCH_ERROR_KEYS does not map, i.e. the FE↔BE seam has drifted. The
        // map-completeness test exists so that fails in CI rather than in front of a
        // customer; this branch is the safety net, not the plan.
        message = data.error || data.message || message;
      }
    }
    return { message, detail };
  };

  /**
   * The post-commit document step, as its OWN unit of work.
   *
   * Every POST is latched on success, so a Retry resumes at the first one that is still
   * missing and re-sends nothing that already worked (the `sdpsWritten` pattern, applied
   * to the other end of the flow). Any failure is rethrown as a `ProposalGenerationError`
   * carrying the original error AND the still-missing EANs — the switch write is never
   * part of what gets replayed.
   */
  const generateProposals = async (
    targets: PendingProposal[],
    headers: Record<string, string>
  ): Promise<void> => {
    for (const target of targets) {
      if (proposalsGenerated.current.has(proposalKey(target))) continue;
      try {
        await axios.post(
          `${apiBase}/api/v1/contracts/generateContract/proposal`,
          {
            ean: target.ean,
            // NOT a signature. `name` is rendered as `signatureName` into the docx for the
            // PROPOSAL as well (generate.ts -> generateInjection/OfftakeContract), so it is
            // a required generator input, not signature capture.
            name: signerName,
            company_id: destinationCompanyId,
            direction: target.direction,
            billingInfo: target.billingInfo,
            companyEmail,
          },
          { headers },
        );
      } catch (err) {
        const missing = targets
          .filter((x) => !proposalsGenerated.current.has(proposalKey(x)))
          .map((x) => x.ean);
        throw new ProposalGenerationError(err, [...new Set(missing)]);
      }
      // Latched IMMEDIATELY on success, before the next request can fail.
      proposalsGenerated.current.add(proposalKey(target));
    }
  };

  /**
   * The success tail of a committed switch, shared by the straight-through run and by a
   * proposal-only Retry so the two cannot land on different screens.
   */
  const finishSwitch = (lastSwitchDate: string | null, eanRows: EanOwnershipRow[] | null): void => {
    setSwitchDate(lastSwitchDate);
    // §5a: if Company A held only ONE direction, the other is genuinely FREE after the
    // handover AND the point actually has that direction, offer it — as a NEW onboarding,
    // not a switch. Keyed on the POST-switch rows, so a direction the landing company
    // already holds is never offered, and on the point's service components, so a
    // direction that does not EXIST is never offered (no ownership row can exist for a
    // direction the point does not have, which made it look "free").
    const askDirection = resolveAskPhaseDirection(legs, eanRows, sdpAtriasConfig);
    // Distinguish a DEFINITIVE "nothing to offer" (taken / combined switch / the point
    // has no such direction) from a blind skip — unreadable post-switch rows, or service
    // components we could not read.
    setAskUndetermined(isAskPhaseUndetermined(legs, eanRows, sdpAtriasConfig));
    setPhase(askDirection ? "ask" : "done");
  };

  /**
   * Stage 1 — READ ONLY. contractOverview in customer-switch mode performs no writes on
   * the backend, so this resolves and displays the parameters (and their provenance)
   * without persisting anything. The admin can still cancel with zero side effects.
   */
  const loadPreview = async (selection: SourceSelection = {}) => {
    setPhase("loadingPreview");
    setErrorMessage(null);
      setErrorDetail(null);
    try {
      const leg = legs[0];
      if (!leg) throw new Error("no-legs");
      const res = await axios.post(
        `${apiBase}/api/v1/contracts/contractOverview`,
        buildSwitchLegOverviewPayload({ leg, companyId: destinationCompanyId, ean, sourceSelection: selection }),
        { headers: authHeaders() },
      );
      const overview = res.data as SwitchOverviewResponse;
      setResolution(overview);
      // Seed the pickers with the server's own default choice ([0] = most recent) so the
      // displayed values and the selection can never disagree.
      setSourceSelection((prev) => ({
        injection: prev.injection ?? pickDefaultCandidateId(overview.candidateSources?.injection),
        offtake: prev.offtake ?? pickDefaultCandidateId(overview.candidateSources?.offtake),
      }));
      setPhase("preview");
    } catch (err) {
      {
        const described = describeError(err);
        setErrorMessage(described.message);
        setErrorDetail(described.detail ?? null);
      }
      setFailedPhase("loadingPreview");
      setPhase("error");
    }
  };

  // The direction the ask-phase may add: the one Company A did NOT hold, that NO company
  // holds after the switch, AND that the connection point actually HAS. Null for a
  // combined switch (both already created), when no leg ran, when the other direction is
  // already taken (the D5 shape — the landing company holds it), when the point does not
  // offer that direction at all (an injection-only point has no offtake to onboard), and
  // while the switch has not run yet.
  //
  // 🔴 `sdpAtriasConfig` must be passed here AND at the post-run call in
  // runCustomerSwitch: the live render and the phase decision have to be computed from
  // identical inputs, or the modal can land on "ask" with no direction to render.
  const secondLegDirection = resolveAskPhaseDirection(legs, postSwitchEanRows, sdpAtriasConfig);

  // Mirrors the wizard's handleInjection/OfftakeFormChange exactly, including the
  // >1000 MWh confirmation, the clear-end-date-on-flexible rule and the
  // unknown-volume reset. Kept local so the wizard's own step 3 is untouched.
  const applyVolumeSideEffects = (field: string, value: unknown) => {
    if (field === "annualVolume") {
      const numValue = Number(value);
      if (numValue > 1 && numValue < 1000) setShowVolumeConfirmation(false);
      if (numValue > 1000 && !isAdminOrPartner) setShowVolumeConfirmation(true);
    }
    if (field === "annualVolumeUnknown" && value === true) setShowVolumeConfirmation(false);
  };

  const changeSecondLegInjection = <K extends keyof InjectionFormData>(field: K, value: InjectionFormData[K]) => {
    setSecondLegInjectionForm((prev) => {
      let next: InjectionFormData = { ...prev, [field]: value };
      if (field === "isFlexible" && value === true) next = { ...next, endDate: "" };
      if (field === "annualVolumeUnknown" && value === true) next = { ...next, annualVolume: null };
      return next;
    });
    applyVolumeSideEffects(field as string, value);
  };

  const changeSecondLegOfftake = <K extends keyof OfftakeFormData>(field: K, value: OfftakeFormData[K]) => {
    setSecondLegOfftakeForm((prev) => {
      let next: OfftakeFormData = { ...prev, [field]: value };
      if (field === "isFlexible" && value === true) next = { ...next, endDate: "" };
      if (field === "annualVolumeUnknown" && value === true) next = { ...next, annualVolume: null };
      return next;
    });
    applyVolumeSideEffects(field as string, value);
  };

  /**
   * The optional second direction. A PLAIN ONBOARDING: contractOverview runs in NORMAL
   * mode (the backend detects switch mode from A's foreign rows, which leg 1 already
   * ended, so it takes the normal path by itself), and the contract payload goes out via
   * buildSecondLegContractPayload — "Supplier Switch", switch flags stripped.
   */
  const runSecondLeg = async () => {
    if (!secondLegDirection) return;
    setFormValidationError(null);
    const form = secondLegDirection === "injection" ? secondLegInjectionForm : secondLegOfftakeForm;

    if (!form.startDate) {
      setFormValidationError(t("addservice:errors.startDateRequired"));
      return;
    }
    if (!isValidBasicStartDate(form.startDate)) {
      setFormValidationError(t("addservice:errors.startDateFirstOfMonth"));
      return;
    }
    if (!form.isFlexible && !form.endDate) {
      setFormValidationError(t("addservice:errors.endDateRequired"));
      return;
    }
    if (!form.annualVolumeUnknown && !form.annualVolume) {
      setFormValidationError(
        t(
          secondLegDirection === "injection"
            ? "addservice:errors.annualInjectionVolumeRequired"
            : "addservice:errors.annualOfftakeVolumeRequired"
        )
      );
      return;
    }
    if (!form.partnerId || form.partnerId < 1) {
      setFormValidationError(t("addservice:errors.partnerSelectionRequired"));
      return;
    }

    setPhase("secondLegRunning");
    setStages({
      addEan: "complete",
      addContract: secondLegCommitted ? "complete" : "active",
      proposal: secondLegCommitted ? "active" : "pending",
    });
    const headers = authHeaders();
    // On a post-commit retry the contract already exists, so the failed step is the proposal.
    let currentStage: StageKey = secondLegCommitted ? "proposal" : "addContract";
    try {
      // contractOverview is a pure read, safe to re-run on retry — it re-supplies billingInfo
      // for the proposal below.
      const overviewRes = await axios.post(
        `${apiBase}/api/v1/contracts/contractOverview`,
        buildSecondLegOverviewPayload({
          direction: secondLegDirection,
          companyId: destinationCompanyId,
          ean,
          serviceConfig: findDirectionServiceConfig(sdpAtriasConfig, secondLegDirection),
          form,
          technologies,
        }),
        { headers },
      );

      // The addContract POST is the ONLY write on this leg and it COMMITS before it
      // responds. Once committed the latch is set, so a retry SKIPS this block entirely and
      // resumes at the proposal — re-POSTing addContract here would create a second contract.
      if (!secondLegCommitted) {
        const contractPayload = buildSecondLegContractPayload(overviewRes.data as SwitchOverviewResponse, {
          direction: secondLegDirection,
          companyId: destinationCompanyId,
          postSwitchRows: postSwitchEanRows,
        });
        // Belt-and-braces: a payload that would mislabel this plain onboarding as a
        // Customer Switch must never leave the browser.
        if (!isSecondLegPayloadSafe(contractPayload)) {
          throw new Error("second-leg payload failed the switchType safety check");
        }
        // …and one that would silently no-op against `WHERE installation_id = NULL` must
        // not either. The ask-phase only offers a FREE direction, so the INSERT branch is
        // the only correct outcome here.
        if (!selectsInsertBranch(contractPayload, secondLegDirection)) {
          throw new ContractNotCreatedError([secondLegDirection]);
        }
        // Name the row when we hold one, and only for the direction this call acts on.
        // Held ⇒ an earlier attempt already wrote a draft here; absent ⇒ the field is
        // OMITTED (never null, never "") and the backend derives the row itself.
        const secondLegPayload = {
          ...contractPayload,
          ...buildContractIdFields(contractIdsWritten.current, [secondLegDirection]),
        };
        const addRes = await axios.post(`${apiBase}/api/v1/contracts/addContract`, secondLegPayload, { headers });
        // LATCH BEFORE the body check, exactly like the primary leg: the commit precedes the
        // response, so from here a retry must not write again.
        setSecondLegCommitted(true);
        contractIdsWritten.current = mergeContractIds(contractIdsWritten.current, readAddContractIds(addRes.data));
        // A 200 is not proof: updateContract* returns "" on a zero-row UPDATE and the
        // handler does not treat "" as an error. Read the body.
        const notWritten = missingWrittenDirections(addRes.data, secondLegDirection);
        if (notWritten.length > 0) throw new ContractNotCreatedError(notWritten);
      }
      setStage("addContract", "complete");

      currentStage = "proposal";
      setStage("proposal", "active");
      const proposalPayload = {
        ean,
        // See the switch leg: `name` feeds `signatureName` in the PROPOSAL docx too.
        name: signerName,
        company_id: destinationCompanyId,
        direction: secondLegDirection,
        billingInfo: (overviewRes.data as SwitchOverviewResponse)?.billingInfo ?? null,
        companyEmail,
      };
      // Unsigned, exactly like the switch leg: an admin must not sign for company B either.
      await axios.post(`${apiBase}/api/v1/contracts/generateContract/proposal`, proposalPayload, { headers });
      setStage("proposal", "complete");
      setSecondLegDone(true);
      setPhase("done");
    } catch (err) {
      setStage(currentStage, "failed");
      {
        const described = describeError(err);
        setErrorMessage(described.message);
        setErrorDetail(described.detail ?? null);
      }
      setFailedPhase("secondLegRunning");
      setPhase("error");
    }
  };

  /**
   * Retry RESUMES the phase that failed. It used to be hard-wired to runCustomerSwitch, so
   * a failure in the pure-read preview was "retried" by performing the un-previewed write.
   *
   * Once the backend transaction has committed the switch is never replayed — retrying it
   * would take the normal (non-switch) path, since A's foreign rows are gone, and fail on
   * the body check. Nothing to gain, and it reads to the admin like the switch is unsound.
   *
   * 🔴 THE RULE BELOW IS UNCHANGED, and must stay that way: `running` + `switchCommitted`
   * is still non-retryable. "generatingProposals" is a DIFFERENT phase — the post-commit
   * document step, which replays no write — so it is allowed by the same expression
   * without the switch-replay block being touched at all.
   */
  const canRetry = failedPhase !== null && !(failedPhase === "running" && switchCommitted);

  const retryFailedPhase = () => {
    if (failedPhase === "loadingPreview") return void loadPreview(sourceSelection);
    if (failedPhase === "secondLegRunning") return void runSecondLeg();
    if (failedPhase === "generatingProposals") return void resumeProposals();
    if (failedPhase === "running" && !switchCommitted) return void runCustomerSwitch();
  };

  const changeSource = (direction: "injection" | "offtake", contractId: number) => {
    const next = { ...sourceSelection, [direction]: contractId };
    setSourceSelection(next);
    // Re-resolve so the numbers on screen always reflect the pick.
    void loadPreview(next);
  };

  const runCustomerSwitch = async () => {
    // 🔴 GUARD FIRST — addSdps below is a WRITE. With no legs the contract loop never
    // runs, so nothing else is written, missingWrittenDirections (the whole
    // "200 is not proof" defence) never executes because it lives INSIDE the loop, and
    // the flow lands on "done" telling the admin the contracts were created. An orphan
    // SDP under a false success screen is the worst outcome this flow can produce.
    // loadPreview has always had this guard; the write path did not.
    if (legs.length === 0) {
      setStages({ addEan: "failed", addContract: "pending", proposal: "pending" });
      setErrorMessage(t("management:customerSwitch.errorNoLegs"));
      setErrorDetail(null);
      setFailedPhase(null); // nothing to retry — the inputs themselves are wrong
      setPhase("error");
      return;
    }

    setPhase("running");
    setErrorMessage(null);
    setErrorDetail(null);
    setStages({ addEan: "pending", addContract: "pending", proposal: "pending" });

    const headers = authHeaders();

    let currentStage: StageKey = "addEan";
    let legsProcessed = 0;
    try {
      // 1) Adding EAN — persist the SDP under the NEW owner (currently selected company).
      currentStage = "addEan";
      setStage("addEan", "active");
      // One addSdps per point in the group, each with ITS OWN preswitch response,
      // all under company B — before the single addContract that creates the
      // contracts for the whole group in one transaction.
      //
      // 🔴 EACH WRITE IS LATCHED, and Retry SKIPS what is already written.
      //
      // A group fails partway more often than a single point does: member 2's
      // addSdps can fail after member 1's succeeded. Retry then re-POSTs member 1's
      // `pslFullData` verbatim over an SDP that already exists under B — which is
      // updateInjectionSdpPSL over a live row, the exact replay recorded in the
      // commit latch below as having corrupted `annualenergy`. The switchCommitted
      // latch does not cover this: it only guards writes AFTER the transaction
      // commits, and every one of these happens before it.
      //
      // The latch is a ref so it survives the re-render a retry causes.
      const totalPoints = 1 + groupMembers.length;
      const writes: Array<{ ean: string; body: unknown }> = [
        { ean, body: pslFullData },
        ...groupMembers.map((m) => ({ ean: m.ean, body: m.pslFullData })),
      ];
      for (let i = 0; i < writes.length; i++) {
        setLegProgress({ current: i + 1, total: totalPoints });
        if (sdpsWritten.current.has(writes[i].ean)) continue;
        await axios.post(
          `${apiBase}/api/v1/sdp/addSdps/${destinationCompanyId}`,
          writes[i].body,
          { headers }
        );
        // Latched IMMEDIATELY on success, before the next request can fail.
        sdpsWritten.current.add(writes[i].ean);
      }
      setStage("addEan", "complete");

      // 2) + 3) Adding contract + Signing — once per leg. In practice `legs` always
      // holds exactly ONE entry (see CustomerSwitchLeg above); the loop is kept because
      // it is leg-agnostic, not because a split is produced. Boundary B and the resolved
      // params are BE-authoritative; we only feed the returned overview forward.
      setLegProgress({ current: 0, total: legs.length });
      let lastSwitchDate: string | null = null;
      // Local copy: the phase decision below must not race the setState.
      let latestEanRows: EanOwnershipRow[] | null = null;

      for (let i = 0; i < legs.length; i++) {
        const leg = legs[i];
        setLegProgress({ current: i + 1, total: legs.length });

        // 2) Adding contract
        currentStage = "addContract";
        setStage("addContract", "active");

        const overviewPayload = buildSwitchLegOverviewPayload({ leg, companyId: destinationCompanyId, ean, sourceSelection });

        const overviewRes = await axios.post(`${apiBase}/api/v1/contracts/contractOverview`, overviewPayload, {
          headers,
        });
        const overview = overviewRes.data as SwitchOverviewResponse;
        const billingInfo: BillingInfo | null = (overview?.billingInfo as BillingInfo | null) ?? null;

        // Surface the resolved parameters + their provenance so the admin sees the money
        // before it is written (§4c). candidateSources carries BOTH directions, so the
        // ask-phase can pick a source for the second leg too.
        setResolution(overview);

        const sentGroup = groupMembers.map((m) => ({
          ean: m.ean,
          injectionServiceConfig: m.injectionServiceConfig,
          offtakeServiceConfig: m.offtakeServiceConfig,
        }));
        const addRes = await axios.post(
          `${apiBase}/api/v1/contracts/addContract`,
          buildSwitchLegContractPayload(overview, sourceSelection, sentGroup),
          { headers },
        );
        // 🔴 LATCH FIRST, BEFORE ANY BODY VALIDATION. The backend COMMITS its transaction
        // before it responds, so by the time we hold a response the switch has happened —
        // whatever the body says. Setting this after the check below meant the two cases the
        // check exists for (a 200 with an empty body, and a network failure on an
        // already-committed request) both left the latch false, offered Retry, and Retry
        // replayed runCustomerSwitch — re-running addSdps with pslFullData verbatim, i.e.
        // updateInjectionSdpPSL over B's live SDP. That is the PSL-overwrite mechanism that
        // corrupted annualenergy earlier in this ticket.
        setSwitchCommitted(true);
        // Record which row this leg wrote. RECORDED, NOT RE-SENT: the switch leg goes
        // through performCustomerSwitch and is never replayed, and the ask-phase only
        // ever offers the direction this leg did NOT take.
        contractIdsWritten.current = mergeContractIds(contractIdsWritten.current, readAddContractIds(addRes.data));
        lastSwitchDate = addRes.data?.switchDate ?? addRes.data?.response?.switchDate ?? lastSwitchDate;
        // Same blind spot as the second leg: a 200 does not prove a contract row exists.
        const notWritten = missingWrittenDirections(addRes.data, leg.direction);
        if (notWritten.length > 0) throw new ContractNotCreatedError(notWritten);

        // 🔴 The same defence, extended to the OTHER points of the group. The check
        // above reads the top-level injection/offtake, which describe the PRIMARY
        // point only — so without this a sibling whose contract was never written
        // would be reported as a full success.
        if (sentGroup.length > 0) {
          const returned = readGroupMembers(addRes.data);
          // FAIL LOUD 1 — a group was sent and no receipt came back. Never fall back
          // to the primary-only check: that is precisely the silent-success path.
          if (returned === null) {
            throw new ContractNotCreatedError(sentGroup.map((m) => m.ean));
          }
          // FAIL LOUD 2 — the receipt must describe exactly the points we sent
          // (plus the primary). A mismatch means the backend acted on a different
          // set than the admin confirmed.
          if (!groupMemberSetMatches(sentGroup, returned)) {
            throw new ContractNotCreatedError(sentGroup.map((m) => m.ean));
          }
          const expected = new Map(groupMembers.map((m) => [m.ean, m.directions]));
          const memberFailures = membersMissingContracts(returned, expected);
          if (memberFailures.length > 0) throw new ContractNotCreatedError(memberFailures);
        }
        // The post-commit re-read of the EAN's ownership rows. Best-effort on the backend,
        // so it may be null — resolveAskPhaseDirection then declines to offer anything
        // rather than guessing against unknown state.
        latestEanRows = readPostSwitchEanRows(addRes.data) ?? latestEanRows;
        setPostSwitchEanRows(latestEanRows);
        setStage("addContract", "complete");

        // 3) Generating the proposal PDF. JediMaster 2026-07-28 (reversing OQ-10): the
        // Customer Switch GENERATES the contract PDF and does NOT sign it — nobody at
        // Yuso signs on another company's behalf. The proposal is left awaiting
        // signature, which is handled outside this feature.
        currentStage = "proposal";
        setStage("proposal", "active");

        // One proposal per point of the group — the endpoint is keyed on (ean, direction),
        // so a group of two produces two documents. Company B signs each one separately.
        const proposalTargets: PendingProposal[] = [
          { ean, direction: leg.direction, billingInfo },
          ...groupMembers.map((member) => ({
            ean: member.ean,
            direction: member.directions.length === 2 ? "combined" : member.directions[0],
            billingInfo,
          })),
        ];

        // 🔴 Only the FINAL leg's proposals are independently resumable. Resuming an
        // EARLIER leg's documents would leave the remaining legs' contracts un-run while
        // reporting success, so those keep the old (non-retryable) behaviour. In practice
        // `legs` always holds exactly one entry, so this is the live path.
        if (i === legs.length - 1) {
          pendingProposals.current = proposalTargets;
          pendingCompletion.current = { switchDate: lastSwitchDate, eanRows: latestEanRows };
        }
        await generateProposals(proposalTargets, headers);

        // Keep the stage active between legs; only the final leg completes it.
        legsProcessed += 1;
        if (i === legs.length - 1) {
          setStage("proposal", "complete");
        } else {
          // Reopen the contract stage for the next leg.
          setStage("addContract", "active");
        }
      }

      // Belt-and-braces for the same class: never report success without having gone
      // through the contract+proposal path at least once.
      if (legsProcessed === 0) throw new ContractNotCreatedError(["injection", "offtake"]);

      finishSwitch(lastSwitchDate, latestEanRows);
    } catch (err) {
      // No silent catch — surface the failed stage + a real message.
      setStage(currentStage, "failed");
      // 🔴 A post-commit PROPOSAL failure is resumable; a post-commit SWITCH failure is
      // not. Only the former is a ProposalGenerationError, and only when the failing leg
      // recorded resumable targets. Everything else keeps `failedPhase = "running"`, so
      // the switch-replay block in `canRetry` behaves exactly as before.
      const proposalFailure =
        err instanceof ProposalGenerationError && pendingProposals.current.length > 0 ? err : null;
      {
        const described = describeError(proposalFailure ? proposalFailure.original : err);
        setErrorMessage(described.message);
        setErrorDetail(described.detail ?? null);
      }
      setMissingProposalEans(proposalFailure ? proposalFailure.missingEans : []);
      setFailedPhase(proposalFailure ? "generatingProposals" : "running");
      setPhase("error");
    }
  };

  /**
   * Retry for the post-commit document step ONLY. It re-enters at the proposal stage,
   * regenerates just the EANs still missing one (`proposalsGenerated` latch), and then
   * runs the same success tail the straight-through path does. It touches no write:
   * addSdps, addContract and the switch transaction are all behind it.
   */
  const resumeProposals = async () => {
    setPhase("running");
    setErrorMessage(null);
    setErrorDetail(null);
    setStages({ addEan: "complete", addContract: "complete", proposal: "active" });
    try {
      await generateProposals(pendingProposals.current, authHeaders());
      setStage("proposal", "complete");
      setMissingProposalEans([]);
      setFailedPhase(null);
      finishSwitch(pendingCompletion.current?.switchDate ?? null, pendingCompletion.current?.eanRows ?? null);
    } catch (err) {
      setStage("proposal", "failed");
      const proposalFailure = err instanceof ProposalGenerationError ? err : null;
      {
        const described = describeError(proposalFailure ? proposalFailure.original : err);
        setErrorMessage(described.message);
        setErrorDetail(described.detail ?? null);
      }
      setMissingProposalEans(proposalFailure ? proposalFailure.missingEans : []);
      setFailedPhase("generatingProposals");
      setPhase("error");
    }
  };

  if (!isOpen) return null;

  // "preview" is dismissable because nothing has been written yet — contractOverview in
  // switch mode is a pure read. "running" is not: writes are in flight.
  // "loadingPreview" is dismissable for the same reason, and it MUST be: it is now the
  // modal's opening screen, so without it a slow/hanging contractOverview would trap the
  // admin behind a modal with no close button and a dead backdrop.
  const dismissable = phase === "loadingPreview" || phase === "preview" || phase === "error";

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-switch-title"
      onClick={dismissable ? onClose : undefined}
    >
      <div
        className="relative bg-white dark:bg-primary-900 rounded-lg shadow-xl w-full max-w-3xl mx-4 p-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {dismissable && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t("management:customerSwitch.close")}
            className="absolute top-4 right-4 text-primary-400 hover:text-primary-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {phase === "loadingPreview" && (
          <div className="py-12 text-center">
            <svg className="w-8 h-8 mx-auto text-primary-600 animate-spin mb-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-primary-500 text-sm">{t("management:customerSwitch.loadingPreview")}</p>
          </div>
        )}

        {phase === "preview" && resolution && (
          <>
            <div className="mb-6">
              <h1 id="customer-switch-title" className="text-primary-600 text-2xl font-semibold mb-2">
                {t("management:customerSwitch.previewTitle")}
              </h1>
              <p className="text-primary-500 text-sm">{t("management:customerSwitch.previewIntro")}</p>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              {resolution.resolvedParams?.injection && (
                <DirectionParams
                  direction="injection"
                  params={resolution.resolvedParams.injection}
                  provenance={resolution.provenance?.injection ?? null}
                  candidates={resolution.candidateSources?.injection}
                  selectedContractId={sourceSelection.injection}
                  onSelectSource={(id) => changeSource("injection", id)}
                />
              )}
              {resolution.resolvedParams?.offtake && (
                <DirectionParams
                  direction="offtake"
                  params={resolution.resolvedParams.offtake}
                  provenance={resolution.provenance?.offtake ?? null}
                  candidates={resolution.candidateSources?.offtake}
                  selectedContractId={sourceSelection.offtake}
                  onSelectSource={(id) => changeSource("offtake", id)}
                />
              )}
              {!resolution.resolvedParams?.injection && !resolution.resolvedParams?.offtake && (
                <p className="text-sm text-gray-500">{t("management:customerSwitch.noResolvedParams")}</p>
              )}
            </div>

            {resolution.switchDate && (
              <p className="text-primary-400 text-xs mt-4">
                {t("management:customerSwitch.successSwitchDate", { date: resolution.switchDate })}
              </p>
            )}

            <div className="mt-8 flex justify-end gap-3">
              {/* Cancel, not Back — there is no screen behind the preview any more. Safe
                  to leave from here: nothing has been written yet. */}
              <button
                type="button"
                onClick={onClose}
                className="py-2.5 px-4 rounded-md border border-gray-300 text-sm font-semibold text-primary-600 hover:bg-gray-50"
              >
                {t("management:customerSwitch.close")}
              </button>
              <button
                type="button"
                onClick={runCustomerSwitch}
                className="py-2.5 px-5 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold"
              >
                {t("management:customerSwitch.confirmAndCreate")}
              </button>
            </div>
          </>
        )}

        {(phase === "running" || phase === "error") && (
          <>
            <div className="mb-6 text-center">
              <h1 id="customer-switch-title" className="text-primary-600 text-2xl font-semibold">
                {t("management:customerSwitch.running")}
              </h1>
            </div>
            <div className="mx-auto max-w-md">
              {STAGE_ORDER.map((key) => (
                <StageRow
                  key={key}
                  label={t(
                    key === "addEan"
                      ? "management:customerSwitch.stageAddingEan"
                      : key === "addContract"
                        ? "management:customerSwitch.stageAddingContract"
                        : "management:customerSwitch.stageGeneratingProposal",
                  )}
                  state={stages[key]}
                  suffix={
                    (key === "addContract" || key === "proposal") && legProgress.total > 1
                      ? `(${legProgress.current}/${legProgress.total})`
                      : undefined
                  }
                />
              ))}
            </div>
            {phase === "error" && (
              <>
                <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4 text-sm text-[#D04D4D]">
                  <p>{errorMessage}</p>
                  {errorDetail && (
                    <p className="mt-2 text-xs text-[#D04D4D] opacity-90 whitespace-pre-line">{errorDetail}</p>
                  )}
                  {/* The switch itself committed — only documents are missing. Naming the
                      EANs is the part that costs the admin the most without it. */}
                  {failedPhase === "generatingProposals" && missingProposalEans.length > 0 && (
                    <p className="mt-3 text-sm font-medium">
                      {t("management:customerSwitch.errorProposalsMissing", {
                        eans: missingProposalEans.join(", "),
                      })}
                    </p>
                  )}
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="py-2.5 px-4 rounded-md border border-gray-300 text-sm font-semibold text-primary-600 hover:bg-gray-50"
                  >
                    {t("management:customerSwitch.close")}
                  </button>
                  {canRetry && (
                    <button
                      type="button"
                      onClick={retryFailedPhase}
                      className="py-2.5 px-4 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold"
                    >
                      {t("management:customerSwitch.retry")}
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* §5a ask-phase: the switch leg is signed; offer the direction A did not hold. */}
        {phase === "ask" && secondLegDirection && (
          <div className="py-4">
            <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-green-100 mb-5">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 id="customer-switch-title" className="text-primary-600 text-2xl font-semibold mb-2 text-center">
              {t("management:customerSwitch.success")}
            </h1>
            {switchDate && (
              <p className="text-primary-500 text-sm mb-6 text-center">
                {t("management:customerSwitch.successSwitchDate", { date: switchDate })}
              </p>
            )}
            <div className="rounded-lg border border-gray-200 dark:border-primary-700 p-5 mt-4">
              <h2 className="text-primary-600 font-semibold text-base mb-1">
                {t("management:customerSwitch.askTitle", {
                  direction: t(
                    secondLegDirection === "injection" ? "addservice:services.injection" : "addservice:services.offtake"
                  ).toLowerCase(),
                })}
              </h2>
              <p className="text-primary-500 text-sm">{t("management:customerSwitch.askIntro")}</p>
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setPhase("done")}
                  className="py-2.5 px-4 rounded-md border border-gray-300 text-sm font-semibold text-primary-600 hover:bg-gray-50"
                >
                  {t("management:customerSwitch.askDecline")}
                </button>
                <button
                  type="button"
                  onClick={() => setPhase("secondLegParams")}
                  className="py-2.5 px-5 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold"
                >
                  {t("management:customerSwitch.askAccept")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* The extracted step-3 component, rendered IN PLACE with popup-owned state. Only
            the second direction is rendered — the other one simply is not mounted. */}
        {phase === "secondLegParams" && secondLegDirection === "injection" && (
          <InjectionContractParameters
            injectionFormData={secondLegInjectionForm}
            handleInjectionFormChange={changeSecondLegInjection}
            pslData={addressLine ?? null}
            selectedUserCompany={{ companyName }}
            getMinEndDate={getMinEndDate}
            showVolumeConfirmation={showVolumeConfirmation}
            isAdminOrPartner={isAdminOrPartner}
            user={user}
            formValidationError={formValidationError}
            injectionError={null}
            offtakeError={null}
            handleFormSubmit={() => void runSecondLeg()}
            partner={{
              partners,
              partnerContacts,
              selectedPartnerLocal,
              partnerSearchTerm,
              setPartnerSearchTerm,
              showPartnerDropdown,
              setShowPartnerDropdown,
              handlePartnerChange,
              handlePartnerContactChange,
              lastSelectedInjectionPartnerId,
              setLastSelectedInjectionPartnerId,
            }}
          />
        )}
        {phase === "secondLegParams" && secondLegDirection === "offtake" && (
          <OfftakeContractParameters
            offtakeFormData={secondLegOfftakeForm}
            handleOfftakeFormChange={changeSecondLegOfftake}
            pslData={addressLine ?? null}
            selectedUserCompany={{ companyName }}
            getMinEndDate={getMinEndDate}
            showVolumeConfirmation={showVolumeConfirmation}
            isAdminOrPartner={isAdminOrPartner}
            user={user}
            formValidationError={formValidationError}
            injectionError={null}
            offtakeError={null}
            handleFormSubmit={() => void runSecondLeg()}
            partner={{
              partners,
              partnerContacts,
              selectedPartnerLocal,
              partnerSearchTerm,
              setPartnerSearchTerm,
              showPartnerDropdown,
              setShowPartnerDropdown,
              handlePartnerChange,
              handlePartnerContactChange,
              lastSelectedOfftakePartnerId,
              setLastSelectedOfftakePartnerId,
            }}
          />
        )}

        {phase === "secondLegRunning" && (
          <>
            <div className="mb-6 text-center">
              <h1 id="customer-switch-title" className="text-primary-600 text-2xl font-semibold">
                {t("management:customerSwitch.secondLegRunning")}
              </h1>
            </div>
            <div className="mx-auto max-w-md">
              <StageRow label={t("management:customerSwitch.stageAddingContract")} state={stages.addContract} />
              <StageRow label={t("management:customerSwitch.stageGeneratingProposal")} state={stages.proposal} />
            </div>
          </>
        )}

        {phase === "done" && (
          <div className="text-center py-6">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
              <svg className="w-9 h-9 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 id="customer-switch-title" className="text-primary-600 text-2xl font-semibold mb-2">
              {t("management:customerSwitch.success")}
            </h1>
            {switchDate && (
              <p className="text-primary-500 text-sm mb-2">{t("management:customerSwitch.successSwitchDate", { date: switchDate })}</p>
            )}
            {/* Must not imply the contracts are signed — they are generated proposals. */}
            <div className="mx-auto max-w-md rounded-md bg-yellow-50 border border-yellow-200 p-3 mb-2">
              <p className="text-sm text-yellow-800">{t("management:customerSwitch.successUnsigned")}</p>
            </div>
            {secondLegDone && <p className="text-primary-500 text-sm mb-8">{t("management:customerSwitch.secondLegDone")}</p>}
            {askUndetermined && (
              <div className="mx-auto max-w-md rounded-md bg-yellow-50 border border-yellow-200 p-3 mb-8 text-left">
                <p className="text-sm text-yellow-800">{t("management:customerSwitch.askUndetermined")}</p>
              </div>
            )}
            {!secondLegDone && !askUndetermined && <div className="mb-8" />}
            <button
              type="button"
              onClick={onSuccess}
              className="py-3 px-6 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold"
            >
              {t("management:customerSwitch.done")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
